import CloseIcon from "@assets/icons/new/close_24px.svg?react";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { BasicTabProps } from "@components/common/tabs/tabs-type";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { Box, Grid2, Typography } from "@mui/material";
import { AccountSettingsTabs } from "@router/route-params";
import { useAppSelector } from "@store/store-helper";
import { loggedInUserSelector } from "@store/user/user-selector";
import { neutral } from "@flat-ui";
import {
  DEFAULT_BOLD_TEXT_FONT_WEIGHT,
  DEFAULT_TEXT_FONT_SIZE,
  DEFAULT_TEXT_FONT_WEIGHT,
  DEFAULT_TITLE_FONT_SIZE,
} from "@styles/common-styles";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { MouseEvent } from "react";

interface Props {
  /** The tabs for account settings */
  tabs: Record<AccountSettingsTabs, BasicTabProps<AccountSettingsTabs>>;

  /** The selected tab */
  selectedTabId: AccountSettingsTabs;
}

/** The mobile layout for account settings tab contents */
export function AccountSettingsMobileTabContents({
  tabs,
  selectedTabId,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const { navigateToRoot } = useAppNavigation();
  const selectedTab = tabs[selectedTabId];

  const filteredTabContents = selectedTab.content.filter(
    (selectedTabContent) => !selectedTabContent.isHidden
  );

  function onClose(event: MouseEvent<HTMLButtonElement>): void {
    trackEvent({
      name: OpenTabEvents.openAccountSettingsTab,
      props: {
        tab: "Tab selection list",
        isUserLoggedIn: !!loggedInUser,
      },
    });

    // Navigate to root if the user is not logged in
    // Otherwise, follow the href link in the IconButton
    if (!loggedInUser) {
      event.preventDefault();
      navigateToRoot();
    }
  }
  return (
    <>
      {/* Header for the tab content */}
      <Grid2
        container
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ px: "15px", mt: "45px" }}
      >
        <Typography
          sx={{
            color: neutral[800],
            fontSize: "1.5rem",
          }}
        >
          {selectedTab.label}
        </Typography>
        <SphereIconButton
          component={CloseIcon}
          href={".."}
          onClick={onClose}
          iconButtonProps={{
            // Adjust alignment of the close icon in small screen
            sx: {
              width: "2rem",
              height: "2rem",
            },
          }}
        />
      </Grid2>

      {/* Body for the tab content */}
      <Box sx={{ mt: "20px", mx: "15px" }}>
        {filteredTabContents.map((section, index) => (
          <Box key={index} sx={{ mt: index === 0 ? "20px" : "80px" }}>
            <Typography
              sx={{
                fontSize: DEFAULT_TITLE_FONT_SIZE,
                fontWeight: DEFAULT_BOLD_TEXT_FONT_WEIGHT,
                color: neutral[800],
                mb: "8px",
              }}
            >
              {section.title}
            </Typography>
            <Typography
              sx={{
                fontSize: DEFAULT_TEXT_FONT_SIZE,
                fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
                lineHeight: "18px",
                color: neutral[800],
                mb: "24px",
              }}
            >
              {section.subtitle}
            </Typography>
            {section.items.map((item, index) => (
              <Box key={index}>{item.content}</Box>
            ))}
          </Box>
        ))}
      </Box>
    </>
  );
}
